import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { transformFormDataToBackend } from "../../utils/transformFormData";
import { useNavigate } from "react-router-dom";
import { useLoader } from "../../utils/LoaderContext";
import { notifySuccess, notifyError } from "../../utils/ToastifyProvider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ORDERS_PER_PAGE = 20;

const getTokenOrThrow = () => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Token not found");
  return token;
};

// ---------------------- Get All Orders ----------------------
const fetchAllOrders = async ({
  currentPage,
  ORDERS_PER_PAGE,
  status,
  rtoStatus,
  search = "",
  startDate,
  userId,
  endDate,
  downloadStatus,
  PaymentType,
  verify,
  decline,
}) => {
  const token = getTokenOrThrow();

  const { data } = await axios.get(`${BACKEND_URL}/orders/all-orders`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: {
      page: currentPage,
      limit: ORDERS_PER_PAGE,
      tab: status || "", // 👈 status maps to tab
      search,
      userId,
      rtoStatus,
      startDate,
      endDate,
      downloadStatus, // 👈 send to API
      PaymentType,
      verify, // 🆕 send to API
      decline,
    },
  });

  return data;
};

export const useAllOrders = ({
  currentPage = 1,
  ORDERS_PER_PAGE = 20,
  status = "",
  rtoStatus,
  search = "",
  startDate,
  userId,
  endDate,
  downloadStatus = "",
  PaymentType = "",
  verify, // 🆕
  decline,
}) => {
  const navigate = useNavigate();
  const { setLoading } = useLoader();

  return useQuery({
    queryKey: [
      "allOrders",
      currentPage,
      ORDERS_PER_PAGE,
      status,
      search,
      startDate,
      endDate,
      userId,
      downloadStatus, // 👈 included in queryKey
      rtoStatus,
      PaymentType,
      verify, // 🆕
      decline,
    ],
    queryFn: async () => {
      setLoading(true);
      try {
        return await fetchAllOrders({
          currentPage,
          ORDERS_PER_PAGE,
          status,
          search,
          startDate,
          endDate,
          userId,
          downloadStatus,
          rtoStatus,
          PaymentType,
          verify, // ✅ add this
          decline,
        });
      } finally {
        setLoading(false);
      }
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
    onError: (error) => {
      if (error.message === "Token not found") {
        notifyError("Please login to continue.");
        navigate("/login");
      } else {
        notifyError("Failed to fetch orders.");
      }
    },
  });
};

// ---------------------- Generate Order Number ----------------------
const fetchGeneratedOrderNumber = async () => {
  const token = getTokenOrThrow();
  const res = await axios.get(`${BACKEND_URL}/orders/generate-order-number`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const useGenerateOrderNumber = () => {
  const navigate = useNavigate();
  const { setLoading } = useLoader();

  return useQuery({
    queryKey: ["generatedOrderNumber"],
    queryFn: async () => {
      setLoading(true);
      try {
        return await fetchGeneratedOrderNumber();
      } finally {
        setLoading(false);
      }
    },
    refetchOnWindowFocus: false,
    retry: false,
    onError: (error) => {
      setLoading(false);
      if (error.message === "Token not found") {
        notifyError("Please login to continue.");
        navigate("/login");
      } else {
        notifyError("Failed to generate order number.");
      }
    },
  });
};

// ---------------------- Create Order ----------------------
const createOrder = async (submitData) => {
  const token = getTokenOrThrow();

  const response = await axios.post(
    `${BACKEND_URL}/orders/create`,
    submitData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    }
  );

  notifySuccess("Order created successfully!");
  return response.data;
};

export const useCreateOrder = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setLoading } = useLoader();

  return useMutation({
    mutationFn: async (formData) => {
      setLoading(true);
      try {
        return await createOrder(formData);
      } finally {
        setLoading(false);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["allOrders"]);
      navigate("/orders?tab=shipping");
    },
    onError: (error) => {
      setLoading(false);
      if (error.message === "Token not found") {
        notifyError("Please login to continue.");
        navigate("/login");
      } else {
        notifyError("Failed to create order.");
        console.error(
          "❌ Order creation failed:",
          error.response?.data || error
        );
      }
    },
  });
};

// ---------------------- Cancel Order ----------------------
export const cancelOrder = async (shipcluesOrderId) => {
  const token = getTokenOrThrow();
  const res = await axios.post(
    `${BACKEND_URL}/orders/cancel/${shipcluesOrderId}`,
    {},
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  notifySuccess("Order cancelled successfully!");
  return res.data;
};

export const useCancelOrder = () => {
  const queryClient = useQueryClient();
  const { setLoading } = useLoader();

  return useMutation({
    mutationFn: async (shipcluesOrderId) => {
      setLoading(true);
      try {
        return await cancelOrder(shipcluesOrderId);
      } finally {
        setLoading(false);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["allOrders"]);
    },
    onError: (error) => {
      setLoading(false);
      console.error("❌ Cancel order failed:", error);
      notifyError("Something went wrong while cancelling the order.");
    },
  });
};

// ---------------------- Fetch All Products ----------------------
const fetchProducts = async () => {
  const token = getTokenOrThrow();
  const res = await axios.get(`${BACKEND_URL}/products`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data.products;
};

export const useAllProducts = () => {
  const navigate = useNavigate();
  const { setLoading } = useLoader();

  return useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      setLoading(true);
      try {
        return await fetchProducts();
      } finally {
        setLoading(false);
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
    onError: (error) => {
      setLoading(false);
      if (error.message === "Token not found") {
        notifyError("Please login to continue.");
        navigate("/login");
      } else {
        notifyError("Failed to fetch products.");
      }
    },
  });
};

// ---------------------- Fetch Pincode Details ----------------------
const fetchPincodeDetails = async (pincode) => {
  const apiKey = "8e4bc4a08ac84c0a871094ddd50280a7"; // Replace with your real API key
  const url = `https://api.geoapify.com/v1/geocode/search?text=${pincode}&filter=countrycode:in&apiKey=${apiKey}`;

  const res = await axios.get(url);
  const feature = res.data.features?.[0];

  if (!feature) throw new Error("Invalid pincode or no data found");

  const { state, state_district, country } = feature.properties;

  return {
    state: state || "Unknown",
    city: state_district,
    country: country || "India",
  };
};

export const usePincodeDetails = (pincode) => {
  const { setLoading } = useLoader();

  return useQuery({
    queryKey: ["pincodeDetails", pincode],
    queryFn: async () => {
      setLoading(true);
      try {
        return await fetchPincodeDetails(pincode);
      } finally {
        setLoading(false);
      }
    },
    enabled: !!pincode,
    retry: 1,
    staleTime: 10 * 60 * 1000,
    onError: (error) => {
      setLoading(false);
      console.error("❌ Pincode fetch failed:", error);
      notifyError("Failed to fetch pincode details.");
    },
  });
};

const fetchPincodeServiceability = async (pincode) => {
  const res = await axios.post(`${BACKEND_URL}/orders/check-pincode`, {
    pincode,
  });
  return res.data;
};

export const useCheckPincodeServiceability = (pincode) => {
  return useQuery({
    queryKey: ["checkPincode", pincode],
    queryFn: () => fetchPincodeServiceability(pincode),
    enabled: !!pincode && pincode.length === 6, // Only run if pincode is 6 digits
    refetchOnWindowFocus: false,
    retry: false,
  });
};

// ---------------------- Get Available Courier Partners ----------------------
export const getAvailablePartners = async (payload) => {
  const { data } = await axios.post(
    `${BACKEND_URL}/orders/get-available-partners`,
    payload
  );

  return data.data.rates;
};

export const useCourierPartners = (payload) => {
  const { setLoading } = useLoader();

  return useQuery({
    queryKey: ["available-couriers", payload],
    queryFn: async () => {
      setLoading(true);
      try {
        return await getAvailablePartners(payload);
      } finally {
        setLoading(false);
      }
    },
    enabled: !!payload,
    onError: (error) => {
      setLoading(false);
      console.error("❌ Courier partner fetch failed:", error);
      notifyError("Failed to fetch courier partners.");
    },
  });
};

// ---------------------- Ship Order With Partner ----------------------
const shipOrderWithPartner = async (payload) => {
  const token = getTokenOrThrow();

  const res = await fetch(`${BACKEND_URL}/orders/ship-and-manifest`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data.message || "Failed to ship order");
  }

  notifySuccess(data.message || "Order shipped successfully!");
  return data;
};

export const useShipOrder = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setLoading } = useLoader();

  return useMutation({
    mutationFn: async (payload) => {
      setLoading(true);
      try {
        return await shipOrderWithPartner(payload);
      } finally {
        setLoading(false);
      }
    },
    onSuccess: (data) => {
      setLoading(false);
      
      // Invalidate all order queries to refresh the list
      queryClient.invalidateQueries(["orders"]);
      queryClient.invalidateQueries(["all-orders"]);
      
      // Navigate to manifest tab
      navigate("/orders?tab=manifest");
    },
    onError: (error) => {
      setLoading(false);
      console.error("❌ Failed to ship order:", error);
      notifyError(error.message || "Failed to ship order");
    },
  });
};

export const useDownloadLabel = () => {
  const { setLoading } = useLoader();

  return useMutation({
    mutationFn: async (orderIds) => {
      // ✅ orderIds can be a single ID (string) or array of IDs
      const orderIdParam = Array.isArray(orderIds)
        ? orderIds.join(",")
        : orderIds;

      setLoading(true);
      
      try {
        const token = getTokenOrThrow();
        
        const response = await axios.get(
          `${BACKEND_URL}/orders/download-label?order_id=${orderIdParam}`,
          { 
            responseType: "blob",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          Array.isArray(orderIds)
            ? `Labels-${orderIds.length}-${Date.now()}.pdf`
            : `Label-${orderIds}-${Date.now()}.pdf`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url); // ✅ Clean up

        notifySuccess(
          Array.isArray(orderIds)
            ? `${orderIds.length} label(s) downloaded successfully!`
            : "Label downloaded successfully!"
        );

        return response.data;
      } catch (error) {
        console.error("❌ Failed to download label(s):", error);
        notifyError(
          error.response?.data?.message || "Failed to download label(s)"
        );
        throw error;
      } finally {
        setLoading(false);
      }
    },
  });
};

export const useGenerateManifest = () => {
  const token = localStorage.getItem("token");
  const queryClient = useQueryClient();
  const { setLoading } = useLoader(); // Optional, if using a global loader

  return useMutation({
    mutationFn: async (orderId) => {
      setLoading?.(true); // ✅ Optional global loader ON
      try {
        const res = await fetch(`${BACKEND_URL}/orders/generate-manifest`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`, // ✅ Add token here
          },
          body: JSON.stringify({ orderId }),
        });

        if (!res.ok) {
          const error = await res.json();
          throw new Error(error?.message || "Failed to generate manifest");
        }

        return await res.json(); // return manifest response
      } finally {
        setLoading?.(false); // ✅ Optional global loader OFF
      }
    },

    onSuccess: (data) => {
      // console.log("✅ Manifest generated:", data);
      notifySuccess("Manifest generated successfully!");

      // Optionally refetch related queries
      queryClient.invalidateQueries(["orders"]);
    },

    onError: (error) => {
      console.error("❌ Manifest generation failed:", error);
      notifyError(error.message || "Failed to generate manifest");
    },
  });
};

export const useDownloadManifest = () => {
  return useMutation({
    mutationFn: async (manifestId) => {
      if (!manifestId) throw new Error("Manifest ID is required");

      const token = localStorage.getItem("token");
      const res = await fetch(
        `${BACKEND_URL}/orders/download-manifest?manifestId=${manifestId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Server error: ${errorText || res.status}`);
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `manifest-${manifestId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      return { manifestId }; // Optional return value
    },

    onSuccess: (data) => {
      notifySuccess(` Manifest downloaded successfully!`);
    },

    onError: (error) => {
      notifyError(`❌ Download failed: ${error.message}`);
    },

    onSettled: () => {
      // console.log("Download attempt finished (success or error)");
      // Optional: set local state, refresh list, etc.
    },
  });
};

// export const useSearchOrder = (query) => {
//   return useQuery({
//     queryKey: ["search-order", query],
//     queryFn: async () => {
//       if (!query) return null;
//       const token = localStorage.getItem("token");
//       const { data } = await axios.get(
//         `${BACKEND_URL}/orders/search-order?query=${query}`,
//         {
//           headers: {
//             Authorization: `Bearer ${token}`, // ⬅️ Add auth header
//           },
//         }
//       );
//       return data;
//     },
//     enabled: !!query, // only run when query is truthy
//     staleTime: 1000 * 60 * 5, // 5 minutes
//     onError: (error) => {
//       notifyError(error.response?.data?.message || "Failed to search order.");
//     },
//   });
// };

export const useSearchOrders = (searchText, status) => {
  const token = localStorage.getItem("token");
  return useQuery({
    queryKey: ["search-orders", searchText, status],
    enabled: !!searchText,
    queryFn: async () => {
      const { data } = await axios.get(
        `${BACKEND_URL}/orders/all-orders?search=${
          searchText || ""
        }&tab=${status}`,
        {
          headers: {
            Authorization: `Bearer ${token}`, // ⬅️ Add auth header
          },
        }
      );
      return data.orders;
    },
    staleTime: 1000 * 60 * 5,
    onError: (error) => {
      notifyError(error.response?.data?.message || "Failed to search orders.");
    },
  });
};

export const useExportOrders = () => {
  return useMutation({
    mutationFn: async ({ startDate, endDate }) => {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${BACKEND_URL}/orders/export-orders`, {
        params: { startDate, endDate },
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `orders-${startDate}-to-${endDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      return true;
    },
    onSuccess: () => {
      notifySuccess("Orders exported successfully!");
    },
    onError: (error) => {
      console.error(error);
      notifyError(error.response?.data?.message || "Failed to export orders");
    },
  });
};

const ndrReattempt = async (payload) => {
  const { data } = await axios.post(
    `${BACKEND_URL}/orders/ndr-reattempt`,
    payload
  );
  return data;
};

export const useNdrReattempt = () => {
  return useMutation({
    mutationFn: ndrReattempt,
    onSuccess: (response) => {
      notifySuccess(
        response?.message || "Reattempt request sent successfully!"
      );
    },
    onError: (error) => {
      notifyError(
        error?.response?.data?.message || "Failed to send reattempt request"
      );
    },
  });
};

const postDownloadStatus = async (body) => {
  const response = await axios.post(
    `${BACKEND_URL}/orders/download-status`,
    body
  );
  return response.data;
};

export const useDownloadStatusMutation = () => {
  return useMutation({
    mutationFn: postDownloadStatus,
    onSuccess: (data) => {
      notifySuccess("Order Downloaded");
    },
    onError: (error) => {
      notifyError("❌ Error:", error);
    },
  });
};
// you can show toast or alert here
// ---------------------- RTO Accept ----------------------
const rtoAccept = async (orderId) => {
  const token = getTokenOrThrow();

  const { data } = await axios.post(
    `${BACKEND_URL}/warehouse/rto-accept`,
    { orderId },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return data;
};

export const useRtoAccept = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: rtoAccept,
    onSuccess: (data) => {
      notifySuccess("Order accepted successfully");
      queryClient.invalidateQueries(["allOrders"]);
    },
    onError: () => {
      notifyError("Failed to accept order");
    },
  });
};

// ---------------------- RTO Reject ----------------------
const rtoReject = async ({ orderId, remark }) => {
  const token = getTokenOrThrow();

  const { data } = await axios.post(
    `${BACKEND_URL}/warehouse/rto-reject`,
    { orderId, remark },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return data;
};

export const useRtoReject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: rtoReject,
    onSuccess: () => {
      notifySuccess("Order rejected successfully");
      queryClient.invalidateQueries(["allOrders"]);
    },
    onError: () => {
      notifyError("Failed to reject order");
    },
  });
};

const fetchDashboard = async ({ startDate, endDate }) => {
  const token = getTokenOrThrow();

  const { data } = await axios.get(`${BACKEND_URL}/orders/dashboard`, {
    params: { startDate, endDate },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return data.data;
};

export const useDashboard = ({ startDate, endDate }) => {
  return useQuery({
    queryKey: ["dashboard", startDate, endDate],
    queryFn: () => fetchDashboard({ startDate, endDate }),
    keepPreviousData: true,
  });
};

// -----------user orders---------

const getUserOrders = async ({ userId, page = 1, limit = 20 }) => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("Token not found");

  const { data } = await axios.get(`${BACKEND_URL}/orders/all-orders`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: {
      userId,
      page,
      limit,
    },
  });

  return data;
};

// ✅ React Query hook
export const useUserOrders = ({ userId, page = 1, limit = 20 }) => {
  return useQuery({
    queryKey: ["userOrders", userId, page, limit],
    queryFn: () => getUserOrders({ userId, page, limit }),
    enabled: !!userId, // only run if we actually have a userId
    keepPreviousData: true,
    onError: (err) => {
      console.error("Error fetching user orders:", err);
      notifyError("Failed to fetch user orders");
    },
  });
};

const verifyOrderAPI = async (orderId) => {
  const token = getTokenOrThrow();
  const { data } = await axios.patch(
    `${BACKEND_URL}/orders/${orderId}/verify`,
    {},
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return data;
};

export const useVerifyOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: verifyOrderAPI,
    onSuccess: (data) => {
      notifySuccess("Order verified successfully!");
      // Invalidate related queries to refetch data
      queryClient.invalidateQueries(["allOrders"]);
    },
    onError: (error) => {
      console.error("❌ Verify order failed:", error);
      notifyError(error.response?.data?.message || "Failed to verify order");
    },
  });
};

const declineOrderAPI = async ({ orderId, remarks }) => {
  const token = getTokenOrThrow();
  const { data } = await axios.put(
    `${BACKEND_URL}/orders/${orderId}/decline`,
    { remarks }, // Send remarks in the request body
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return data;
};

export const useDeclineOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: declineOrderAPI,
    onSuccess: (data) => {
      notifySuccess("Order declined successfully!");
      // Invalidate related queries to refetch data
      queryClient.invalidateQueries(["allOrders"]);
    },
    onError: (error) => {
      console.error("❌ Decline order failed:", error);
      notifyError(error.response?.data?.message || "Failed to decline order");
    },
  });
};

// 🔄 Reverify Order (Reset verification status)
const reverifyOrderAPI = async (orderId) => {
  const token = getTokenOrThrow();
  const { data } = await axios.patch(
    `${BACKEND_URL}/orders/${orderId}/reverify`,
    {},
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return data;
};

export const useReverifyOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: reverifyOrderAPI,
    onSuccess: (data) => {
      notifySuccess("Order status reset successfully!");
      // Invalidate related queries to refetch data
      queryClient.invalidateQueries(["allOrders"]);
    },
    onError: (error) => {
      console.error("❌ Reverify order failed:", error);
      notifyError(
        error.response?.data?.message || "Failed to reset order status"
      );
    },
  });
};

const updatePaymentMethodAPI = async ({ orderId, methodIndex, paymentOption, paymentAmount, paymentProofImages }) => {
  const token = getTokenOrThrow();
  
  const formData = new FormData();
  formData.append('methodIndex', methodIndex);
  if (paymentOption) formData.append('paymentOption', paymentOption);
  if (paymentAmount !== undefined) formData.append('paymentAmount', paymentAmount);
  
  // Append new image files if any
  if (paymentProofImages && paymentProofImages.length > 0) {
    paymentProofImages.forEach(file => {
      formData.append('paymentProofImages', file);
    });
  }

  const { data } = await axios.put(
    `${BACKEND_URL}/orders/${orderId}/payment-method`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return data;
};

export const useUpdatePaymentMethod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updatePaymentMethodAPI,
    onSuccess: (data) => {
      notifySuccess("Payment method updated successfully!");
      queryClient.invalidateQueries(["allOrders"]);
    },
    onError: (error) => {
      console.error("❌ Update payment method failed:", error);
      notifyError(error.response?.data?.error || "Failed to update payment method");
    },
  });
};

// ---------------------- Delete Payment Image ----------------------
const deletePaymentImageAPI = async ({ orderId, methodIndex, imageUrl }) => {
  const token = getTokenOrThrow();

  const { data } = await axios.delete(
    `${BACKEND_URL}/orders/${orderId}/payment-method`,
    {
      headers: { Authorization: `Bearer ${token}` },
      data: { methodIndex, imageUrl },
    }
  );
  return data;
};

export const useDeletePaymentImage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deletePaymentImageAPI,
    onSuccess: (data) => {
      notifySuccess("Image deleted successfully!");
      queryClient.invalidateQueries(["allOrders"]);
    },
    onError: (error) => {
      console.error("❌ Delete image failed:", error);
      notifyError(error.response?.data?.error || "Failed to delete image");
    },
  });
};